import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useHomework, useSyncChild } from '../api/homework';
import { FuelGauge } from './FuelGauge';
import { useSyncStatus } from '../hooks/useSyncStatus';

export function HomeworkPage() {
  const { data, isLoading, error } = useHomework();
  const syncChildMutation = useSyncChild();
  const { syncStatus } = useSyncStatus();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Refetch data when sync completes (use invalidateQueries to bypass staleTime)
  useEffect(() => {
    if (!syncStatus.isActive && syncStatus.lastCompleted) {
      queryClient.invalidateQueries({ queryKey: ['homework'] });
    }
  }, [syncStatus.isActive, syncStatus.lastCompleted, queryClient]);

  const handleChildSync = (childName: string) => {
    syncChildMutation.mutate(childName);
  };

  const formatLastSync = (dateString: string | Date | null) => {
    if (!dateString) return 'Never';
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading homework data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Error loading homework</h3>
          <p className="text-red-600 text-sm mt-1">
            {error instanceof Error ? error.message : 'An unknown error occurred'}
          </p>
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['homework'] })}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const homework = data?.data || [];
  const meta = data?.meta;
  const allChildren = ['Alexander', 'Thomas'];

  return (
    <div className="container mx-auto p-4 sm:p-6">
      {/* Real-time sync status */}
      {syncStatus.isActive && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <div>
            <p className="text-blue-900 font-medium">Sync in progress</p>
            {syncStatus.message && (
              <p className="text-blue-700 text-sm">{syncStatus.message}</p>
            )}
            {syncStatus.currentChild && (
              <p className="text-blue-600 text-xs mt-1">Processing: {syncStatus.currentChild}</p>
            )}
          </div>
        </div>
      )}

      {/* Homework cards — always render one card per child */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {allChildren.map((childName) => {
          const child = homework.find((c) => c.childName === childName);
          const isChildSyncing = syncStatus.isActive && syncStatus.currentChild === childName;
          const isSyncPending = syncChildMutation.isPending;

          if (!child) {
            return (
              <div key={childName} className="bg-gray-50 border border-gray-200 rounded-lg shadow-sm p-6 flex flex-col items-center justify-center gap-4 min-h-[160px]">
                <p className="text-gray-600 font-medium">{childName}</p>
                <p className="text-gray-400 text-sm">No homework data</p>
                <button
                  onClick={() => handleChildSync(childName)}
                  disabled={isSyncPending || isChildSyncing}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <svg className={`h-4 w-4 ${isChildSyncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {isChildSyncing ? 'Syncing…' : `Sync ${childName}`}
                </button>
              </div>
            );
          }

          return (
            <div
              key={childName}
              className="bg-white border border-gray-200 rounded-lg shadow-sm p-6"
            >
              {/* Child header with refresh button */}
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {child.childName}
                  </h2>
                  {child.lastSyncedAt && (
                    <p className="text-xs text-gray-500 mt-1">
                      Updated {formatLastSync(child.lastSyncedAt)}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleChildSync(child.childName)}
                  disabled={isSyncPending || isChildSyncing}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                  title={`Refresh ${child.childName}'s homework`}
                >
                  {isChildSyncing ? (
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Homework gauges */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-4">
                <FuelGauge
                  percentage={child.homework.maths?.completionPercentage ?? null}
                  label="Maths"
                  color="blue"
                  dueDate={child.homework.maths?.dueDate}
                  hasError={child.homework.maths?.hasError}
                  errorMessage={child.homework.maths?.errorMessage}
                  assignmentId={child.homework.maths?.assignmentId}
                  onClick={() => child.homework.maths?.assignmentId && navigate(`/homework/${child.homework.maths.assignmentId}`)}
                />
                <FuelGauge
                  percentage={child.homework.science?.completionPercentage ?? null}
                  label="Science"
                  color="green"
                  dueDate={child.homework.science?.dueDate}
                  hasError={child.homework.science?.hasError}
                  errorMessage={child.homework.science?.errorMessage}
                  assignmentId={child.homework.science?.assignmentId}
                  onClick={() => child.homework.science?.assignmentId && navigate(`/homework/${child.homework.science.assignmentId}`)}
                />
                <FuelGauge
                  percentage={child.homework.reader?.completionPercentage ?? null}
                  label="Reader"
                  color="purple"
                  dueDate={child.homework.reader?.dueDate}
                  hasError={child.homework.reader?.hasError}
                  errorMessage={child.homework.reader?.errorMessage}
                  assignmentId={child.homework.reader?.assignmentId}
                  onClick={() => child.homework.reader?.assignmentId && navigate(`/homework/${child.homework.reader.assignmentId}`)}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Sync status indicator */}
      {meta?.lastSyncStatus === 'error' && meta.lastSyncError && (
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 text-sm">
            <span className="font-semibold">Last sync had errors:</span> {meta.lastSyncError}
          </p>
        </div>
      )}
    </div>
  );
}
